import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

interface ExtendedFormData {
  firstName: string;
  lastName: string;
  age: string;
  email: string;
  diocese: string;
  prierePour: string;
  engagement: string;
  intention: string;
  newsletter: boolean;
}

// Configuration Supabase
const supabaseUrl = 'https://tdzhjkcbyywdyoqqxjga.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRkemhqa2NieXl3ZHlvcXF4amdhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDczMTk3ODEsImV4cCI6MjA2Mjg5NTc4MX0.6L3HhupUI4W9c_dBdGBqUsx2Y7n5W0xh26xkezZCEjY';
const supabase = createClient(supabaseUrl, supabaseKey);

const FormulaireLong: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId?: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(userId ? true : false);
  const [formData, setFormData] = useState<ExtendedFormData>({
    firstName: '',
    lastName: '',
    age: '',
    email: '',
    diocese: '',
    prierePour: '',
    engagement: 'quotidien',
    intention: '',
    newsletter: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Effet pour charger les données utilisateur depuis Supabase si un ID est fourni dans l'URL
  useEffect(() => {
    if (userId) {
      const fetchUserData = async () => {
        try {
          setIsLoading(true);
          
          // Récupérer les données de l'utilisateur depuis Supabase
          const { data, error } = await supabase
            .from('conclave_participants')
            .select('*')
            .eq('id', userId)
            .single();
          
          if (error) {
            console.error('Erreur lors de la récupération des données:', error);
            setErrorMessage(`Impossible de trouver les données utilisateur: ${error.message}`);
            setSubmitStatus('error');
          } else if (data) {
            // Convertir les données Supabase au format du formulaire
            setFormData({
              firstName: data.first_name || '',
              lastName: data.last_name || '',
              age: data.age ? String(data.age) : '',
              email: data.email || '',
              diocese: data.diocese || '',
              prierePour: data.priere_pour || '',
              engagement: data.engagement || 'quotidien',
              intention: data.intention || '',
              newsletter: data.newsletter === undefined ? true : data.newsletter
            });
            console.log('Données utilisateur chargées:', data);
          } else {
            setErrorMessage('Aucune donnée trouvée pour cet identifiant');
            setSubmitStatus('error');
          }
        } catch (err) {
          console.error('Exception lors du chargement des données:', err);
          setErrorMessage('Une erreur est survenue lors du chargement des données');
          setSubmitStatus('error');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchUserData();
    }
  }, [userId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    // Gestion spéciale pour les cases à cocher
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation basique
    if (!formData.firstName || !formData.lastName || !formData.age || !formData.email) {
      setErrorMessage('Les champs de base (prénom, nom, âge, email) sont obligatoires.');
      setSubmitStatus('error');
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      setErrorMessage('Veuillez entrer une adresse email valide.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Préparation des données pour Supabase
      const userData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        age: parseInt(formData.age),
        email: formData.email,
        diocese: formData.diocese,
        priere_pour: formData.prierePour,
        engagement: formData.engagement,
        intention: formData.intention,
        newsletter: formData.newsletter,
        updated_at: new Date().toISOString()
      };

      // Si userId existe, mettre à jour les données existantes, sinon créer un nouvel enregistrement
      if (userId) {
        // Mettre à jour les données existantes
        const { error: updateError } = await supabase
          .from('conclave_participants')
          .update(userData)
          .eq('id', userId);

        if (updateError) {
          throw new Error(`Erreur lors de la mise à jour: ${updateError.message}`);
        }

        console.log(`Données mises à jour pour l'ID: ${userId}`);
      } else {
        // Créer un nouvel enregistrement
        const { error: insertError } = await supabase
          .from('conclave_participants')
          .insert([{ ...userData, created_at: new Date().toISOString() }]);

        if (insertError) {
          throw new Error(`Erreur lors de l'insertion: ${insertError.message}`);
        }
      }

      // Envoyer également l'email de confirmation
      const emailResponse = await fetch('/.netlify/functions/send-email-extended', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!emailResponse.ok) {
        const errorData = await emailResponse.json();
        console.warn('Avertissement: Email non envoyé, mais données sauvegardées:', errorData);
      }
      
      // Stocker dans la base de données secondaire
      try {
        console.log('Envoi des données à la base secondaire...');
        const secondaryResponse = await fetch('/.netlify/functions/store-data-secondary', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        
        if (secondaryResponse.ok) {
          console.log('Données enregistrées avec succès dans la base secondaire');
        } else {
          const secondaryErrorData = await secondaryResponse.json();
          console.warn('Avertissement: Échec de l\'enregistrement dans la base secondaire:', secondaryErrorData);
        }
      } catch (secondaryError) {
        console.error('Erreur lors de l\'enregistrement dans la base secondaire:', secondaryError);
        // Nous continuons même en cas d'erreur avec la base secondaire
      }

      setSubmitStatus('success');

      // Rediriger vers la page d'accueil après 10 secondes
      setTimeout(() => {
        navigate('/');
      }, 10000);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur inattendue est survenue');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 sm:p-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-xl border border-gray-700">
      <motion.h2
        className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mb-6 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        Formulaire de participation spirituelle
      </motion.h2>

      <motion.p
        className="text-gray-300 mb-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        Partagez plus d'informations pour personnaliser votre expérience de prière pendant le conclave.
      </motion.p>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-300">Chargement des données...</p>
        </div>
      ) : submitStatus === 'success' ? (
        <motion.div
          className="bg-green-900/50 p-4 rounded-lg text-center mb-6"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <p className="text-green-200 font-bold mb-2">✓ Formulaire envoyé avec succès !</p>
          <p className="text-green-300">
            Merci pour votre engagement spirituel. Vous allez être redirigé vers la page d'accueil dans 10 secondes...
          </p>
        </motion.div>
      ) : (
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="space-y-6"
        >
          {submitStatus === 'error' && (
            <motion.div
              className="bg-red-900/50 p-4 rounded-lg text-center mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <p className="text-red-200">{errorMessage}</p>
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Informations de base (questions originales) */}
            <div className="space-y-4">
              <h3 className="text-yellow-500 font-semibold border-b border-gray-700 pb-2">
                Informations personnelles
              </h3>
              
              <div>
                <label className="block text-gray-300 mb-2" htmlFor="firstName">
                  Prénom *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
                  placeholder="Votre prénom"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2" htmlFor="lastName">
                  Nom *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
                  placeholder="Votre nom"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2" htmlFor="age">
                  Âge *
                </label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
                  placeholder="Votre âge"
                  min="1"
                  max="120"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2" htmlFor="email">
                  Adresse email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
                  placeholder="votre@email.com"
                  required
                />
              </div>
            </div>

            {/* Questions supplémentaires */}
            <div className="space-y-4">
              <h3 className="text-yellow-500 font-semibold border-b border-gray-700 pb-2">
                Informations spirituelles
              </h3>
              
              {/* Question 1 */}
              <div>
                <label className="block text-gray-300 mb-2" htmlFor="diocese">
                  Votre diocèse
                </label>
                <input
                  type="text"
                  id="diocese"
                  name="diocese"
                  value={formData.diocese}
                  onChange={handleChange}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
                  placeholder="Ex: Paris, Lyon, Marseille..."
                />
              </div>

              {/* Question 2 */}
              <div>
                <label className="block text-gray-300 mb-2" htmlFor="prierePour">
                  Pour qui souhaitez-vous prier principalement ?
                </label>
                <select
                  id="prierePour"
                  name="prierePour"
                  value={formData.prierePour}
                  onChange={handleChange}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
                >
                  <option value="">Sélectionnez...</option>
                  <option value="cardinal">Mon cardinal attribué</option>
                  <option value="college">Le collège des cardinaux</option>
                  <option value="pape">Le futur pape</option>
                  <option value="eglise">L'Église universelle</option>
                </select>
              </div>

              {/* Question 3 */}
              <div>
                <label className="block text-gray-300 mb-2">
                  Fréquence de votre engagement de prière
                </label>
                <div className="flex flex-col space-y-2">
                  <label className="inline-flex items-center text-gray-300">
                    <input
                      type="radio"
                      name="engagement"
                      value="quotidien"
                      checked={formData.engagement === 'quotidien'}
                      onChange={handleChange}
                      className="mr-2 text-yellow-500 focus:ring-yellow-500"
                    />
                    Quotidien
                  </label>
                  <label className="inline-flex items-center text-gray-300">
                    <input
                      type="radio"
                      name="engagement"
                      value="hebdomadaire"
                      checked={formData.engagement === 'hebdomadaire'}
                      onChange={handleChange}
                      className="mr-2 text-yellow-500 focus:ring-yellow-500"
                    />
                    Hebdomadaire
                  </label>
                  <label className="inline-flex items-center text-gray-300">
                    <input
                      type="radio"
                      name="engagement"
                      value="occasionnel"
                      checked={formData.engagement === 'occasionnel'}
                      onChange={handleChange}
                      className="mr-2 text-yellow-500 focus:ring-yellow-500"
                    />
                    Occasionnel
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Questions qui prennent toute la largeur */}
          <div className="space-y-4 pt-4 border-t border-gray-700">
            {/* Question 4 */}
            <div>
              <label className="block text-gray-300 mb-2" htmlFor="intention">
                Avez-vous une intention de prière particulière pour le conclave ?
              </label>
              <textarea
                id="intention"
                name="intention"
                value={formData.intention}
                onChange={handleChange}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white"
                placeholder="Partagez votre intention de prière..."
                rows={3}
              />
            </div>

            {/* Question 5 */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="newsletter"
                name="newsletter"
                checked={formData.newsletter}
                onChange={handleChange}
                className="w-5 h-5 text-yellow-500 focus:ring-yellow-500 rounded"
              />
              <label className="ml-3 text-gray-300" htmlFor="newsletter">
                Souhaitez-vous recevoir des nouvelles du conclave par email ?
              </label>
            </div>
          </div>

          <div className="flex justify-between items-center pt-4">
            <Link
              to="/"
              className="px-5 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Retour
            </Link>

            <motion.button
              type="submit"
              className="px-5 py-3 bg-gradient-to-r from-yellow-600 to-yellow-800 text-white font-bold rounded-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Envoi en cours...
                </span>
              ) : (
                'Envoyer'
              )}
            </motion.button>
          </div>
        </motion.form>
      )}
    </div>
  );
};

export default FormulaireLong;
